import * as THREE from "three";
import { TILE_PX } from "./TileAtlas";

/** The crack art below is laid out 16 to a block; this scales it to the tiles' density. */
const K = TILE_PX / 16;

/** Sharp close up, blended from mip levels further out (as the tiles are, see atlasTexture). */
function crackTexture(c: HTMLCanvasElement): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(c);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  return tex;
}

/** Five stages of cracks spreading across a block, as it is dug out (or shot up). */
export const CRACKS: THREE.CanvasTexture[] = [1, 2, 3, 4, 5].map((stage) => {
  const c = document.createElement("canvas");
  c.width = c.height = TILE_PX;
  const ctx = c.getContext("2d")!;
  ctx.fillStyle = `rgba(0,0,0,${0.08 * stage})`;
  ctx.fillRect(0, 0, TILE_PX, TILE_PX);
  ctx.strokeStyle = "rgba(10,8,6,0.85)";
  ctx.lineWidth = 1;
  // Each stage adds more of the same jagged lines out from the middle.
  const lines = [
    [8, 8, 3, 2, 1, 5],
    [8, 8, 13, 4, 15, 2],
    [8, 8, 5, 13, 2, 15],
    [8, 8, 12, 12, 14, 15],
    [8, 8, 8, 1, 10, 0],
    [8, 8, 1, 9, 0, 11],
    [8, 8, 15, 9, 15, 7],
    [8, 8, 9, 15, 7, 16],
  ];
  for (const [x0, y0, x1, y1, x2, y2] of lines.slice(0, stage + 2)) {
    jag(ctx, [x0!, y0!, x1!, y1!, x2!, y2!]);
  }
  return crackTexture(c);
});

/** The crack texture for a block `fraction` (0..1) of the way to breaking. */
export function cracksAt(fraction: number): THREE.CanvasTexture {
  return CRACKS[Math.min(CRACKS.length - 1, Math.max(0, Math.floor(fraction * CRACKS.length)))]!;
}

type Ctx = CanvasRenderingContext2D;

/** A jagged line through the points [x0, y0, x1, y1, ...] (16 to a block), centred on pixels. */
function jag(ctx: Ctx, pts: readonly number[]): void {
  ctx.beginPath();
  ctx.moveTo(pts[0]! * K + 0.5, pts[1]! * K + 0.5);
  for (let k = 2; k < pts.length; k += 2) ctx.lineTo(pts[k]! * K + 0.5, pts[k + 1]! * K + 0.5);
  ctx.stroke();
}

/** A filled shape through the points [x0, y0, x1, y1, ...] (16 to a block). */
function blot(ctx: Ctx, pts: readonly number[]): void {
  ctx.beginPath();
  ctx.moveTo(pts[0]! * K, pts[1]! * K);
  for (let k = 2; k < pts.length; k += 2) ctx.lineTo(pts[k]! * K, pts[k + 1]! * K);
  ctx.closePath();
  ctx.fill();
}

/** Chips knocked off a block's corners: dark notches where the face has gone. */
const CHIPS = [
  [0, 0, 4, 0, 2, 2, 0, 3],
  [16, 16, 11, 16, 13, 14, 16, 12],
  [16, 0, 16, 4, 14, 2, 13, 0],
];

/** Whole chunks blown out of the face, showing the dark inside. */
const HOLES = [
  [0, 0, 6, 0, 5, 3, 2, 5, 0, 4],
  [16, 9, 16, 15, 12, 16, 10, 13, 13, 10],
  [6, 16, 3, 13, 5, 12, 8, 14, 9, 16],
];

/**
 * Battle damage, in three stages: hairline cracks after a first hit, then
 * wider cracks with chipped corners, then (one more and it goes) deep cracks,
 * chunks blown out and scorching. Each tiles across a piece. Separate from
 * CRACKS, which show digging progress.
 */
export const DAMAGE_STAGES: THREE.CanvasTexture[] = [0, 1, 2].map((stage) => {
  const c = document.createElement("canvas");
  c.width = c.height = TILE_PX;
  const ctx = c.getContext("2d")!;
  if (stage === 2) {
    // Scorched: soot spreading out from the worst of it.
    const g = ctx.createRadialGradient(9 * K, 9 * K, K, 9 * K, 9 * K, 10 * K);
    g.addColorStop(0, "rgba(20,12,6,0.55)");
    g.addColorStop(1, "rgba(30,20,10,0.12)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, TILE_PX, TILE_PX);
  } else if (stage === 1) {
    ctx.fillStyle = "rgba(0,0,0,0.1)";
    ctx.fillRect(0, 0, TILE_PX, TILE_PX);
  }
  ctx.lineCap = "square";
  // Hairlines: two thin, faint cracks.
  ctx.strokeStyle = stage === 0 ? "rgba(10,8,6,0.55)" : "rgba(10,8,6,0.85)";
  ctx.lineWidth = 1;
  jag(ctx, [7, 7, 4, 5, 3, 2, 1, 1]);
  jag(ctx, [7, 7, 10, 9, 12, 13, 14, 15]);
  if (stage >= 1) {
    jag(ctx, [7, 7, 11, 5, 13, 3, 15, 3]);
    jag(ctx, [7, 7, 5, 11, 2, 12, 0, 14]);
    ctx.fillStyle = "rgba(12,9,7,0.8)";
    for (const chip of CHIPS.slice(0, stage === 1 ? 2 : 3)) blot(ctx, chip);
  }
  if (stage === 2) {
    // The main cracks gape: wide and dark, with a pale broken edge along one side.
    ctx.lineWidth = 3;
    ctx.strokeStyle = "rgba(6,4,3,0.95)";
    jag(ctx, [7, 7, 4, 5, 3, 2]);
    jag(ctx, [7, 7, 10, 9, 12, 13]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgba(200,180,150,0.35)";
    jag(ctx, [8, 6, 5, 4, 4, 1]);
    jag(ctx, [8, 8, 11, 10, 13, 13]);
    jag(ctx, [7, 7, 7, 11, 9, 14]);
    ctx.fillStyle = "rgba(8,6,5,0.93)";
    for (const hole of HOLES) blot(ctx, hole);
  }
  const tex = crackTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
});

/**
 * The battle damage stage (index into DAMAGE_STAGES) for a block `fraction`
 * (hits / toughness) of the way to breaking: an iron plate shows light damage
 * after one hit and heavy after two, a 2-hit block medium after its first.
 */
export function damageStage(fraction: number): number {
  return fraction < 0.4 ? 0 : fraction < 0.6 ? 1 : 2;
}
